import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfig } from '@lore/config';

const QUERY_KEY = ['userNotificationStates', 'list'];

/**
 * @typedef {Object} UserNotificationState
 * @property {number} id
 * @property {number} userId
 * @property {string} itemKey
 * @property {string} itemType
 * @property {string} status
 * @property {string|null} shownAt
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * Custom hook to fetch the authenticated user's notification/CTA states.
 * The backend decides which cards apply to the user (not_applicable items are
 * filtered out server-side), and computes each card's status. The UI is
 * still responsible for ordering/priority and for the visibility of the
 * statsig-gated cards.
 * @param {Object} [params]
 * @param {boolean} [params.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult & { data: UserNotificationState[], statesByKey: Object<string, UserNotificationState>, markShown: Function }}
 */
export default function useUserNotificationStates(params = {}) {
  const { enabled = true } = params;

  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => {
      const url = `${apiRoot}/userNotificationStates?pageSize=100`;
      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch user notification states');
        }
        return res.json();
      });
    },
    staleTime: 0,
    enabled,
  });

  // The backend excludes not_applicable items for regular users, but not for
  // staff users (by design), so filter them out here as well to keep behavior
  // consistent regardless of the requesting user's role.
  const data = useMemo(() => {
    const results = query.data && query.data.results ? query.data.results : [];
    return results.filter((state) => state.status !== 'not_applicable');
  }, [query.data]);

  const statesByKey = useMemo(() => (
    data.reduce((acc, state) => {
      acc[state.itemKey] = state;
      return acc;
    }, {})
  ), [data]);

  // Records that a card has been shown to the user by setting shownAt to now
  // (e.g., on the first display, or after a completed card has animated away, so
  // it is not animated a second time). Merges the updated record returned by
  // the backend into the cached list so that statesByKey reflects the change
  // without a refetching.
  const setShownAtMutation = useMutation({
    mutationFn: ({ itemKey, itemType = 'cta', shownAt }) => {
      const url = `${apiRoot}/userNotificationStates`;
      return fetch(url, {
        method: 'POST',
        headers: {
          ...headers(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ itemKey, itemType, shownAt }),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to update user notification state');
        }
        return res.json();
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(QUERY_KEY, (old) => {
        if (!old || !old.results) return old;
        return {
          ...old,
          results: old.results.map((state) => (
            state.itemKey === updated.itemKey ? { ...state, ...updated } : state
          )),
        };
      });
    },
  });

  const { mutateAsync: setShownAtMutateAsync } = setShownAtMutation;
  const markShown = useCallback((itemKey, itemType = 'cta') => (
    setShownAtMutateAsync({ itemKey, itemType, shownAt: 'now' })
  ), [setShownAtMutateAsync]);

  return {
    ...query,
    data,
    statesByKey,
    markShown,
  };
};
